// ════════════════════════════════════════════════════════════════════
// LYRAEA — Visualizer.js   ·   THE GRACES, MADE TO DANCE
// ────────────────────────────────────────────────────────────────────
// The ten foils of Terror's Foil Vault (King's Grace), each turned into a
// full-stage music visualiser in the spirit of the old Windows Media
// Player ones: frame feedback, trails, tunnels, bars that breathe.
//
// Colours, titles and blurbs MIRROR app-terror/src/kingsGrace.js GRACES.
// That file is not vendored here — if a Grace's colour changes in Terror,
// copy it across by hand. Staleness is fine; silent forking is not.
//
// ── THE THREE TIERS — Terror canon, not invented here ───────────────
// Terror's own mapping (FountainOverlay.js reveal): a FABLE prize wears
// holo_silver, a LEGENDARY prize wears holo_gold, a MYTHIC wears the
// chase foils. TERROR_PRIME: MYTHIC is "rarest, best-designed foils", and
// the Vault foils are one-per-account under THE MYTHICAL LAW. So:
//   FABLE      Silver   — clean and quiet, no glow
//   LEGENDARY  Gold     — fuller: flame, trails, flecks
//   MYTHIC     the other eight — everything at once
// Every visualiser is free: no currency, no store.
//
// ── WHY THE PULSE IS SYNTHETIC ──────────────────────────────────────
// audio.js deliberately does NOT set crossOrigin on its one <audio>
// element. Without CORS, a WebAudio AnalyserNode on that element reads
// silence, and routing the element through a graph MUTES the real
// playback. So these never touch the audio. They move to a pulse seeded
// by the track id: its own tempo, with a kick, a snare and drifting
// bands. The pulse swells while music plays and settles on pause. If
// the CDN ever sends CORS headers, swap `sample()` for real analyser
// bins. Nothing else would need to change.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from "react";
import { getAudioState } from "./audio";

// ── the choice (a per-viewer convenience, same as the lyrics mode) ───
// Two separate things: WHICH Grace is chosen, and whether the stage is
// SHOWING it. The album cover is the default; the Grace is a one-tap swap
// that never forgets which one you picked.
const KEY = "lyraea.vis", SHOW = "lyraea.vis.show";
const read = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
let current = read(KEY, "off");
let showing = read(SHOW, "0") === "1";
const subs = new Set();
const emit = () => subs.forEach((f) => f({ id: current, show: showing }));
export const getVis = () => current;
export function setVisShow(on) {
  showing = !!on && current !== "off";
  try { localStorage.setItem(SHOW, showing ? "1" : "0"); } catch {}
  emit();
}
// Picking a Grace shows it. "off" means go back to the cover and keep the pick.
export function setVis(id) {
  if (id === "off") return setVisShow(false);
  current = id;
  try { localStorage.setItem(KEY, id); } catch {}
  setVisShow(true);
}
function useVisState() {
  const [v, setV] = useState({ id: current, show: showing });
  useEffect(() => { subs.add(setV); return () => subs.delete(setV); }, []);
  return v;
}
export const useVis = () => useVisState().id;
export const useVisShow = () => { const v = useVisState(); return v.show && v.id !== "off"; };

// ── helpers ──────────────────────────────────────────────────────────
const TAU = Math.PI * 2;
const rgb = (hex) => {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const rgba = (hex, a) => { const [r, g, b] = rgb(hex); return `rgba(${r},${g},${b},${a})`; };
const hash = (s) => { let h = 2166136261; for (const ch of String(s)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); } return h >>> 0; };

// Redraw the last frame zoomed or rotated, then veil it. This is the WMP trick:
// everything leaves a trail and the screen turns into a tunnel.
function feedback(ctx, cv, w, h, { zoom = 1.02, rot = 0, dx = 0, dy = 0, fade = 0.1, veil = "0,0,0" }) {
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.translate(w / 2 + dx, h / 2 + dy);
  ctx.rotate(rot);
  ctx.scale(zoom, zoom);
  ctx.drawImage(cv, -w / 2, -h / 2, w, h);
  ctx.restore();
  ctx.fillStyle = `rgba(${veil},${fade})`;
  ctx.fillRect(0, 0, w, h);
}
function particles(st, max) {
  st.p = st.p || [];
  if (st.p.length > max) st.p.splice(0, st.p.length - max);
  return st.p;
}
function stepParticles(ctx, st, draw) {
  const p = st.p || [];
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i];
    q.x += q.vx; q.y += q.vy; q.life -= q.decay;
    if (q.life <= 0) { p.splice(i, 1); continue; }
    draw(q);
  }
}

// ── the pulse ────────────────────────────────────────────────────────
const N = 48;
function sample(sig, t, level, out) {
  const beat = t * sig.bpm / 60 + sig.phase;
  const bp = beat % 1;
  const kick = Math.exp(-bp * 6.5);
  const off = (beat + 0.5) % 1;
  const snare = Math.exp(-off * 9) * (Math.floor(beat) % 2 ? 1 : 0.35);
  const hat = Math.exp(-((beat * 2) % 1) * 14);
  for (let i = 0; i < N; i++) {
    const f = i / (N - 1);
    const drift = 0.5 + 0.5 * Math.sin(t * (1.3 + i * 0.29) + i * 1.7 + sig.phase * 9);
    const swell = 0.55 + 0.45 * Math.sin(t * 0.43 + i * 0.19 + sig.phase * 4);
    const v = (1 - f) ** 2 * kick * 1.05 + Math.exp(-((f - 0.45) ** 2) * 18) * snare * 0.8
      + f * f * hat * 0.55 + drift * swell * (0.42 - f * 0.18);
    const target = Math.min(1, v * level);
    out[i] += (target - out[i]) * (target > out[i] ? 0.55 : 0.18);
  }
  return { kick: kick * level, snare: snare * level, hat: hat * level, beat };
}

// ── THE TEN ──────────────────────────────────────────────────────────
// Each `draw(ctx, w, h, t, S, st, cv)`: S = { bins, bass, mid, high, kick,
// snare, hat, beat, level, c:[color,color2,color3] }, st = per-visual scratch.

function quicksilver(ctx, w, h, t, S, st, cv) {
  feedback(ctx, cv, w, h, { zoom: 1.012, fade: 0.22, veil: "8,10,16" });
  const [c1, c2, c3] = S.c;
  ctx.globalCompositeOperation = "lighter";
  for (let band = 0; band < 2; band++) {
    const dir = band ? -1.35 : 1;
    const g = ctx.createLinearGradient(0, 0, w, 0);
    g.addColorStop(0, rgba(c3, 0.05)); g.addColorStop(0.5, rgba(c2, 0.55)); g.addColorStop(1, rgba(c1, 0.05));
    for (let mirror of [1, -1]) {
      ctx.beginPath();
      for (let x = 0; x <= w; x += 4) {
        const bi = Math.min(N - 1, Math.floor((x / w) * N));
        const amp = h * (0.06 + S.bins[bi] * 0.26);
        const y = h / 2 + mirror * (Math.sin(x * 0.018 * (band ? 1.4 : 1) + t * 2.4 * dir) * amp + band * 10 * mirror);
        x ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      ctx.strokeStyle = g; ctx.lineWidth = 1.5 + S.bass * 4;
      ctx.stroke();
    }
  }
  ctx.shadowBlur = 0;
}

function molten(ctx, w, h, t, S, st, cv) {
  feedback(ctx, cv, w, h, { zoom: 1.01, dy: -3, fade: 0.16, veil: "12,5,0" });
  const [c1, c2, c3] = S.c;
  const bars = 32, bw = w / bars;
  ctx.globalCompositeOperation = "lighter";
  const p = particles(st, 260);
  for (let i = 0; i < bars; i++) {
    const v = S.bins[Math.floor((i / bars) * N)];
    const bh = h * (0.04 + v * 0.62);
    const x = i * bw;
    const g = ctx.createLinearGradient(0, h, 0, h - bh);
    g.addColorStop(0, rgba(c3, 0.9)); g.addColorStop(0.6, rgba(c1, 0.85)); g.addColorStop(1, rgba(c2, 1));
    ctx.fillStyle = g;
    // a tongue of flame, not a box
    ctx.beginPath();
    ctx.moveTo(x + 1, h);
    ctx.quadraticCurveTo(x + bw * 0.1, h - bh * 0.7, x + bw / 2 + Math.sin(t * 6 + i) * bw * 0.3, h - bh);
    ctx.quadraticCurveTo(x + bw * 0.9, h - bh * 0.7, x + bw - 1, h);
    ctx.fill();
    if (Math.random() < v * 0.5) p.push({ x: x + bw / 2, y: h - bh, vx: (Math.random() - 0.5) * 0.8, vy: -1 - Math.random() * 2.5 * (0.5 + S.bass), life: 1, decay: 0.012 + Math.random() * 0.015, r: 1 + Math.random() * 2.2 });
  }
  stepParticles(ctx, st, (q) => { ctx.fillStyle = rgba(c2, q.life); ctx.beginPath(); ctx.arc(q.x, q.y, q.r, 0, TAU); ctx.fill(); });
}

function maelstrom(ctx, w, h, t, S, st, cv) {
  feedback(ctx, cv, w, h, { zoom: 0.965, rot: 0.025 + S.bass * 0.03, fade: 0.05, veil: "10,2,20" });
  const [c1, c2, c3] = S.c;
  const cx = w / 2, cy = h / 2, R = Math.min(w, h) * 0.52;
  ctx.globalCompositeOperation = "lighter";
  const arms = 5;
  for (let a = 0; a < arms; a++) {
    for (let k = 0; k < 26; k++) {
      const f = k / 26;
      const r = R * (0.12 + f * 0.95);
      const ang = t * 1.1 + a * (TAU / arms) + f * 3.4;
      const v = S.bins[Math.floor(f * (N - 1))];
      ctx.fillStyle = rgba(f < 0.4 ? c2 : c1, 0.25 + v * 0.7);
      ctx.beginPath(); ctx.arc(cx + Math.cos(ang) * r, cy + Math.sin(ang) * r, 1.2 + v * 6 * (1 - f * 0.5), 0, TAU); ctx.fill();
    }
  }
  // the throat that swallows the light
  ctx.globalCompositeOperation = "source-over";
  const rr = R * (0.12 + S.bass * 0.08);
  const g = ctx.createRadialGradient(cx, cy, rr * 0.2, cx, cy, rr * 1.6);
  g.addColorStop(0, "rgba(0,0,0,1)"); g.addColorStop(0.6, rgba(c3, 0.95)); g.addColorStop(1, rgba(c1, 0));
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, rr * 1.6, 0, TAU); ctx.fill();
  ctx.strokeStyle = rgba(c2, 0.5 + S.kick * 0.5); ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(cx, cy, rr, 0, TAU); ctx.stroke();
}

function grip(ctx, w, h, t, S, st, cv) {
  feedback(ctx, cv, w, h, { zoom: 1.022, rot: 0.006, fade: 0.16, veil: "6,8,11" });
  const [c1, c2, c3] = S.c;
  const cx = w / 2, cy = h / 2, m = Math.min(w, h);
  // sparks thrown off where the plates bite
  const sp = particles(st, 160);
  if (S.kick > 0.75) for (let i = 0; i < 10; i++) {
    const a = Math.PI / 4 + Math.floor(Math.random() * 4) * (TAU / 4) + (Math.random() - 0.5) * 0.5, v = 2 + Math.random() * 4;
    sp.push({ x: cx + Math.cos(a) * m * 0.3, y: cy + Math.sin(a) * m * 0.3, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 1, decay: 0.03, r: 1.5 });
  }
  ctx.globalCompositeOperation = "lighter";
  stepParticles(ctx, st, (q) => { ctx.fillStyle = rgba(c2, q.life); ctx.fillRect(q.x, q.y, q.r * 2, q.r * 2); });
  // the ring the plates close on
  ctx.globalCompositeOperation = "lighter";
  for (let i = 0; i < 64; i++) {
    const a = (i / 64) * TAU + t * 0.2;
    const v = S.bins[Math.floor((Math.abs(Math.sin(a)) * (N - 1)))];
    const r0 = m * 0.13, r1 = r0 + m * (0.03 + v * 0.17);
    ctx.strokeStyle = rgba(c2, 0.35 + v * 0.6); ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0); ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1); ctx.stroke();
  }
  // four plates clench on the kick: the only one that contracts
  ctx.globalCompositeOperation = "source-over";
  const gap = m * (0.42 - S.kick * 0.14);
  for (let k = 0; k < 4; k++) {
    ctx.save(); ctx.translate(cx, cy); ctx.rotate(k * (TAU / 4) + Math.PI / 4);
    const g = ctx.createLinearGradient(0, -gap, 0, -gap - m * 0.3);
    g.addColorStop(0, rgba(c2, 0.95)); g.addColorStop(0.3, rgba(c1, 0.9)); g.addColorStop(1, rgba(c3, 0.9));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(-m * 0.1, -gap); ctx.lineTo(m * 0.1, -gap); ctx.lineTo(m * 0.22, -gap - m * 0.3); ctx.lineTo(-m * 0.22, -gap - m * 0.3); ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = rgba(c2, 0.6 + S.kick * 0.4); ctx.lineWidth = 1.5; ctx.stroke();
    ctx.restore();
  }
}

function jester(ctx, w, h, t, S, st, cv) {
  feedback(ctx, cv, w, h, { zoom: 1.025, rot: -0.012, fade: 0.16, veil: "0,6,2" });
  const [c1, c2, c3] = S.c;
  const cx = w / 2, cy = h / 2, m = Math.min(w, h);
  ctx.globalCompositeOperation = "lighter";
  const diamond = (x, y, s, rot, col) => {
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot);
    ctx.fillStyle = col; ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(s * 0.6, 0); ctx.lineTo(0, s); ctx.lineTo(-s * 0.6, 0); ctx.closePath(); ctx.fill();
    ctx.restore();
  };
  for (let ring = 0; ring < 2; ring++) {
    const n = ring ? 12 : 8, dir = ring ? -1 : 1;
    const R = m * (ring ? 0.36 : 0.2) * (1 + S.bass * 0.15);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + t * 0.9 * dir;
      const v = S.bins[(i * 3 + ring * 7) % N];
      diamond(cx + Math.cos(a) * R, cy + Math.sin(a) * R, m * (0.02 + v * 0.05), a + t, rgba(i % 2 ? c1 : c2, 0.5 + v * 0.5));
    }
  }
  // something up there grins, and it grins on the beat
  const grin = S.snare;
  if (grin > 0.05) {
    ctx.strokeStyle = rgba(c2, grin); ctx.lineWidth = 3 + grin * 5; ctx.shadowColor = c1; ctx.shadowBlur = 20;
    ctx.beginPath(); ctx.arc(cx, cy - m * 0.05, m * 0.12, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
    ctx.fillStyle = rgba(c2, grin);
    diamond(cx - m * 0.06, cy - m * 0.1, m * 0.018, 0, rgba(c2, grin));
    diamond(cx + m * 0.06, cy - m * 0.1, m * 0.018, 0, rgba(c2, grin));
    ctx.shadowBlur = 0;
  }
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = rgba(c3, 0.035); ctx.fillRect(0, 0, w, h);
}

function caustics(ctx, w, h, t, S, st, cv) {
  const [c1, c2, c3] = S.c;
  const bg = ctx.createLinearGradient(0, 0, 0, h);
  bg.addColorStop(0, rgba(c1, 0.35)); bg.addColorStop(1, rgba(c3, 1));
  ctx.globalCompositeOperation = "source-over"; ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = "lighter";
  // god rays from the surface
  for (let i = 0; i < 6; i++) {
    const x = w * (0.1 + i * 0.16) + Math.sin(t * 0.3 + i) * w * 0.05;
    const g = ctx.createLinearGradient(x, 0, x + w * 0.1, h);
    g.addColorStop(0, rgba(c2, 0.12 + S.bins[i * 7] * 0.15)); g.addColorStop(1, rgba(c2, 0));
    ctx.fillStyle = g; ctx.beginPath(); ctx.moveTo(x - 20, 0); ctx.lineTo(x + 30, 0); ctx.lineTo(x + w * 0.22, h); ctx.lineTo(x + w * 0.05, h); ctx.fill();
  }
  // the light-web: two families of wavering lines crossing each other
  ctx.lineWidth = 1.4;
  for (let fam = 0; fam < 2; fam++) {
    for (let i = 0; i < 14; i++) {
      const v = S.bins[(i * 3 + fam * 5) % N];
      ctx.strokeStyle = rgba(c2, 0.12 + v * 0.45);
      ctx.beginPath();
      for (let s = 0; s <= 40; s++) {
        const u = s / 40;
        const base = (i + 0.5) / 14;
        const wob = Math.sin(u * 9 + t * (1.2 + fam * 0.5) + i * 1.3) * 0.025 * (1 + v * 2) + Math.sin(u * 23 - t * 2 + i) * 0.008;
        const x = fam ? (base + wob) * w : u * w, y = fam ? u * h : (base + wob) * h;
        s ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      ctx.stroke();
    }
  }
  const p = particles(st, 120);
  if (Math.random() < 0.25 + S.hat * 0.8) p.push({ x: Math.random() * w, y: h + 8, vx: 0, vy: -(0.6 + Math.random() * 1.6), life: 1, decay: 0.004, r: 1.5 + Math.random() * 5 * (0.4 + S.high), ph: Math.random() * 9 });
  stepParticles(ctx, st, (q) => {
    q.x += Math.sin(t * 2 + q.ph) * 0.5;
    ctx.strokeStyle = rgba(c2, 0.55 * q.life); ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(q.x, q.y, q.r, 0, TAU); ctx.stroke();
  });
}

function rend(ctx, w, h, t, S, st, cv) {
  feedback(ctx, cv, w, h, { zoom: 1.012, dy: -1.5, fade: 0.13, veil: "14,0,5" });
  const [c1, c2, c3] = S.c;
  const cx = w / 2;
  ctx.globalCompositeOperation = "lighter";
  // the tear: a jagged seam that opens with the bass
  const open = w * (0.004 + S.bass * 0.06);
  st.seed = st.seed || Array.from({ length: 30 }, () => Math.random());
  if (S.kick > 0.9) st.seed = st.seed.map(() => Math.random());
  const seam = (side) => {
    ctx.beginPath();
    for (let i = 0; i <= 29; i++) {
      const y = (i / 29) * h;
      const j = (st.seed[i] - 0.5) * w * 0.05 * (0.4 + S.mid);
      const x = cx + j + side * open * (0.5 + 0.5 * Math.sin((i / 29) * Math.PI));
      i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.stroke();
  };
  ctx.shadowColor = c1; ctx.shadowBlur = 24;
  ctx.strokeStyle = rgba(c2, 0.9); ctx.lineWidth = 2 + S.bass * 3; seam(-1); seam(1);
  ctx.strokeStyle = rgba(c1, 0.7); ctx.lineWidth = 8; seam(0);
  ctx.shadowBlur = 0;
  // flares white on the kick
  if (S.kick > 0.2) {
    const g = ctx.createRadialGradient(cx, h / 2, 0, cx, h / 2, w * 0.6);
    g.addColorStop(0, rgba("#ffffff", S.kick * 0.5)); g.addColorStop(0.3, rgba(c1, S.kick * 0.25)); g.addColorStop(1, rgba(c3, 0));
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  }
  const p = particles(st, 240);
  for (let k = 0; k < 3; k++) if (Math.random() < 0.4 + S.bass) p.push({ x: cx + (Math.random() - 0.5) * open * 3, y: h * (0.3 + Math.random() * 0.7), vx: (Math.random() - 0.5) * 3, vy: -1 - Math.random() * 3, life: 1, decay: 0.012, r: 1 + Math.random() * 2 });
  stepParticles(ctx, st, (q) => { q.vx *= 0.97; ctx.fillStyle = rgba(q.life > 0.6 ? c2 : c1, q.life); ctx.fillRect(q.x, q.y, q.r, q.r * 2.2); });
}

function griddle(ctx, w, h, t, S, st, cv) {
  feedback(ctx, cv, w, h, { zoom: 1.0, dy: -1, fade: 0.28, veil: "10,5,2" });
  const [c1, c2, c3] = S.c;
  const cols = 14, rows = 9;
  // a plate in perspective: cells shrink toward the horizon
  const top = h * 0.38, bot = h * 1.02;
  const sear = (Math.sin(t * 0.25) + 1) / 2; // white-gold → brown → back
  for (let r = 0; r < rows; r++) {
    const f0 = r / rows, f1 = (r + 1) / rows;
    const y0 = top + (bot - top) * f0 * f0, y1 = top + (bot - top) * f1 * f1;
    const wid0 = w * (0.35 + f0 * 0.9), wid1 = w * (0.35 + f1 * 0.9);
    for (let c = 0; c < cols; c++) {
      const v = S.bins[Math.floor(Math.abs(c - cols / 2) / (cols / 2) * (N - 1) * 0.8 + r)] || 0;
      const u0 = c / cols - 0.5, u1 = (c + 1) / cols - 0.5;
      const heat = Math.min(1, v * 1.3);
      const col = heat > 0.6 ? c2 : heat > 0.3 ? c1 : c3;
      ctx.fillStyle = rgba(col, 0.25 + heat * 0.7 * (1 - sear * 0.35));
      ctx.beginPath();
      ctx.moveTo(w / 2 + u0 * wid0 + 1, y0 + 1); ctx.lineTo(w / 2 + u1 * wid0 - 1, y0 + 1);
      ctx.lineTo(w / 2 + u1 * wid1 - 1, y1 - 1); ctx.lineTo(w / 2 + u0 * wid1 + 1, y1 - 1);
      ctx.fill();
    }
  }
  // heat shimmer and curling shreds lifting off the plate
  ctx.globalCompositeOperation = "lighter";
  const p = particles(st, 90);
  if (Math.random() < 0.3 + S.snare) p.push({ x: w * (0.2 + Math.random() * 0.6), y: h * 0.75, vx: 0, vy: -0.8 - Math.random(), life: 1, decay: 0.01, ph: Math.random() * 6, s: 6 + Math.random() * 10 });
  stepParticles(ctx, st, (q) => {
    ctx.strokeStyle = rgba(q.life > 0.5 ? c1 : c3, q.life * 0.9); ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(q.x + Math.sin(t * 3 + q.ph) * 6, q.y, q.s * (1.2 - q.life), q.ph, q.ph + 2.4); ctx.stroke();
  });
  const g = ctx.createLinearGradient(0, 0, 0, top);
  g.addColorStop(0, rgba(c1, 0.02)); g.addColorStop(1, rgba(c1, 0.15 + S.bass * 0.25));
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, top);
}

function halo(ctx, w, h, t, S, st, cv) {
  feedback(ctx, cv, w, h, { zoom: 1.045, rot: 0.004, fade: 0.08, veil: "6,6,20" });
  const [c1, c2, c3] = S.c;
  const cx = w / 2, cy = h / 2, m = Math.min(w, h);
  ctx.globalCompositeOperation = "lighter";
  // one ring leaves on every beat, so the pulse never stops
  st.rings = st.rings || [];
  const bi = Math.floor(S.beat);
  if (bi !== st.lastBeat) { st.lastBeat = bi; st.rings.push({ r: m * 0.05, a: 0.6 + S.level * 0.4 }); }
  for (let i = st.rings.length - 1; i >= 0; i--) {
    const ring = st.rings[i];
    ring.r += m * 0.006 * (1 + ring.r / m * 3); ring.a *= 0.975;
    if (ring.a < 0.02 || ring.r > m) { st.rings.splice(i, 1); continue; }
    ctx.strokeStyle = rgba(i % 2 ? c3 : c2, ring.a); ctx.lineWidth = 2 + ring.a * 4;
    ctx.beginPath(); ctx.ellipse(cx, cy, ring.r, ring.r * 0.92, 0, 0, TAU); ctx.stroke();
  }
  // the crown of rays
  const rays = 24;
  for (let i = 0; i < rays; i++) {
    const a = (i / rays) * TAU + t * 0.15;
    const v = S.bins[(i * 2) % N];
    const r0 = m * 0.07, r1 = r0 + m * (0.05 + v * 0.3);
    const g = ctx.createLinearGradient(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0, cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
    g.addColorStop(0, rgba(c2, 0.9)); g.addColorStop(1, rgba(c3, 0));
    ctx.strokeStyle = g; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0); ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1); ctx.stroke();
  }
  const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, m * (0.08 + S.kick * 0.06));
  core.addColorStop(0, rgba("#ffffff", 1)); core.addColorStop(0.5, rgba(c1, 0.6)); core.addColorStop(1, rgba(c3, 0));
  ctx.fillStyle = core; ctx.beginPath(); ctx.arc(cx, cy, m * 0.16, 0, TAU); ctx.fill();
}

function prism(ctx, w, h, t, S, st, cv) {
  feedback(ctx, cv, w, h, { zoom: 1.03, rot: 0.018 * Math.sin(t * 0.2), fade: 0.09, veil: "4,2,10" });
  const cx = w / 2, cy = h / 2, m = Math.min(w, h);
  ctx.globalCompositeOperation = "lighter";
  // red, green and blue drift apart on the music and meet as white
  const apart = m * (0.02 + S.mid * 0.12 + 0.03 * Math.sin(t * 0.7));
  const chans = ["#ff2a3a", "#2aff6a", "#3a6bff"];
  chans.forEach((col, k) => {
    const a = t * 0.6 + k * (TAU / 3);
    const ox = cx + Math.cos(a) * apart, oy = cy + Math.sin(a) * apart;
    for (let ring = 0; ring < 3; ring++) {
      const R = m * (0.12 + ring * 0.1) * (1 + S.bass * 0.12);
      ctx.strokeStyle = rgba(col, 0.75); ctx.lineWidth = 3 + S.kick * 3;
      ctx.beginPath();
      for (let s = 0; s <= 96; s++) {
        const th = (s / 96) * TAU;
        const v = S.bins[Math.floor((Math.abs(Math.sin(th * 1.5 + ring)) * (N - 1)))];
        const r = R + v * m * 0.05;
        const x = ox + Math.cos(th) * r, y = oy + Math.sin(th) * r;
        s ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      ctx.stroke();
    }
    // spectral petals, sixfold per channel
    for (let i = 0; i < 6; i++) {
      const pa = a * 1.5 + i * (TAU / 6);
      const v = S.bins[(i * 8 + k * 3) % N];
      const r0 = m * 0.42, r1 = r0 + v * m * 0.12;
      ctx.fillStyle = rgba(col, 0.5 * v + 0.1);
      ctx.beginPath(); ctx.ellipse(cx + Math.cos(pa) * (r0 + r1) / 2, cy + Math.sin(pa) * (r0 + r1) / 2, (r1 - r0) / 2 + 3, m * 0.02, pa, 0, TAU); ctx.fill();
    }
  });
  if (S.kick > 0.5) { ctx.fillStyle = rgba("#ffffff", (S.kick - 0.5) * 0.25); ctx.fillRect(0, 0, w, h); }
}

// ── the registry ─────────────────────────────────────────────────────
export const VIS_TIERS = [
  { id: "fable",     label: "FABLE",     color: "#b3a68c", line: "Silver. The base foil, clean and quiet." },
  { id: "legendary", label: "LEGENDARY", color: "#e8b84b", line: "Gold. Molten, with trails and flecks." },
  { id: "mythic",    label: "MYTHIC",    color: "#f43f5e", line: "The chase foils of the Vault. Everything at once." },
];

export const VISUALS = [
  { id: "holo_silver",   tier: "fable",     label: "Silver",   title: "Quicksilver", color: "#cccccc", color2: "#f4f6fb", color3: "#5d6474", glyph: "◈", draw: quicksilver,
    blurb: "Two liquid-metal bands climb and fall, never in step." },
  { id: "holo_gold",     tier: "legendary",     label: "Gold",     title: "Molten",      color: "#ffd700", color2: "#fff3b0", color3: "#ff8a1e", glyph: "✦", draw: molten,
    blurb: "Tongues of molten light lick upward; flecks rise off it." },
  { id: "holo_khao",     tier: "mythic",     label: "Khao",     title: "Maelstrom",   color: "#9333ea", color2: "#d8b4fe", color3: "#1e0536", glyph: "◈", draw: maelstrom,
    blurb: "A throat of violet smoke turns and swallows the light." },
  { id: "holo_gauntlet", tier: "mythic",     label: "Gauntlet", title: "The Grip",    color: "#b9c2cc", color2: "#eef3f8", color3: "#3c444e", glyph: "✦", draw: grip,
    blurb: "Four plates close in and clench on every kick." },
  { id: "holo_jynxi",    tier: "mythic", label: "J'ynxi",   title: "The Jester",  color: "#2ee66b", color2: "#c9ffd9", color3: "#0b5a2a", glyph: "✦", draw: jester,
    blurb: "Emerald diamonds wheel both ways, and something grins." },
  { id: "holo_depths",   tier: "mythic", label: "Depths",   title: "Caustics",    color: "#00aaff", color2: "#b8f0ff", color3: "#003a66", glyph: "◆", draw: caustics,
    blurb: "The light-web of a far surface dances; bubbles rise." },
  { id: "holo_solrend",  tier: "mythic", label: "Solrend",  title: "The Rend",    color: "#ff2266", color2: "#fff0f4", color3: "#7a0022", glyph: "◆", draw: rend,
    blurb: "Sunfire tears the air, flares white, and embers climb." },
  { id: "holo_vrown",    tier: "mythic", label: "Vrown",    title: "The Griddle", color: "#c17a2e", color2: "#fff1c9", color3: "#5a2e0e", glyph: "◆", draw: griddle,
    blurb: "A white-gold plate sears to brown; the shreds curl up." },
  { id: "holo_aeternus", tier: "mythic",  label: "A'eterni", title: "Halo",        color: "#f0f0ff", color2: "#ffffff", color3: "#9aa0ff", glyph: "✦", draw: halo,
    blurb: "A ring of light leaves on every beat. The pulse never stops." },
  { id: "holo_mythic",   tier: "mythic",  label: "Mythic",   title: "Prism",       color: "#ff66cc", color2: "#ffffff", color3: "#5b8cff", glyph: "◆", draw: prism,
    blurb: "Red, green and blue drift apart and recombine into white." },
];
export const visById = (id) => VISUALS.find((v) => v.id === id) || null;

// ── the stage ────────────────────────────────────────────────────────
export default function Visualizer({ visId, seed, playing, style }) {
  const ref = useRef(null);
  const live = useRef({ playing, seed });
  live.current.playing = playing; live.current.seed = seed;

  useEffect(() => {
    const vis = visById(visId);
    const cv = ref.current;
    if (!vis || !cv) return;
    const ctx = cv.getContext("2d");
    const bins = new Float32Array(N);
    const st = {};
    let level = 0, raf = 0, w = 0, h = 0, lastSeed = null, sig = null;
    const t0 = performance.now();

    const fit = () => {
      const r = cv.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = Math.max(1, r.width); h = Math.max(1, r.height);
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, w, h);
    };
    fit();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    ro?.observe(cv);

    const frame = (now) => {
      const t = (now - t0) / 1000;
      const { playing: on, seed: sd } = live.current;
      if (sd !== lastSeed) {
        lastSeed = sd;
        const hs = hash(sd || "lyraea");
        sig = { bpm: 84 + (hs % 57), phase: (hs % 1000) / 1000 };
      }
      // Paused (or still buffering) settles to a gentle idle, never to black.
      level += ((on ? 1 : 0.45) - level) * 0.04;
      const pulse = sample(sig, t, level, bins);
      let bass = 0, mid = 0, high = 0;
      for (let i = 0; i < N; i++) { if (i < 12) bass += bins[i]; else if (i < 32) mid += bins[i]; else high += bins[i]; }
      const S = { bins, bass: bass / 12, mid: mid / 20, high: high / 16, level, ...pulse, c: [vis.color, vis.color2, vis.color3] };
      // Ask for the next frame FIRST: one bad frame must never freeze the stage.
      raf = requestAnimationFrame(frame);
      ctx.save();
      try { vis.draw(ctx, w, h, t, S, st, cv); }
      catch (e) { if (!st.warned) { st.warned = true; console.warn("[Lyraea vis]", vis.id, e); } }
      finally { ctx.restore(); ctx.globalCompositeOperation = "source-over"; ctx.shadowBlur = 0; }
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); };
  }, [visId]);

  return <canvas ref={ref} style={{ width: "100%", height: "100%", display: "block", background: "#000", ...style }} />;
}

// ── the picker: three shelves, a tile per Grace, all free ────────────
export function VisPicker({ onPicked, C }) {
  const shown = useVisShow();
  const picked = useVis();
  const cur = shown ? picked : "off";
  const pick = (id) => { setVis(id); onPicked?.(); };
  const tile = (on) => ({
    textAlign: "left", cursor: "pointer", padding: 0, overflow: "hidden",
    border: `1px solid ${on ? C.goldHi : C.edge}`, background: C.slab,
    boxShadow: on ? `0 0 0 1px ${C.goldHi}, 0 6px 24px #0008` : "none",
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingBottom: 24 }}>
      <button onClick={() => pick("off")} style={{ ...tile(cur === "off"), padding: "12px 14px", color: C.bone, fontFamily: C.mono, fontSize: 11, letterSpacing: ".22em" }}>
        ▣ COVER ART {cur === "off" && "· ON"}
      </button>
      {VIS_TIERS.map((tier) => (
        <section key={tier.id}>
          <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: ".32em", color: tier.color }}>
            {tier.label}
          </div>
          <div style={{ fontFamily: C.sans, fontSize: 12, color: C.smoke, margin: "3px 0 9px" }}>{tier.line}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 9 }}>
            {VISUALS.filter((v) => v.tier === tier.id).map((v) => (
              <button key={v.id} onClick={() => pick(v.id)} style={tile(cur === v.id)}>
                <div className={`ly-vis-swatch ly-vis-${tier.id}`} style={{
                  height: 64, display: "grid", placeItems: "center", fontSize: 26, color: v.color2,
                  textShadow: `0 0 14px ${v.color}`,
                  background: `radial-gradient(circle at 50% 60%, ${v.color}aa, ${v.color3} 70%)`,
                }}>{v.glyph}</div>
                <div style={{ padding: "8px 10px 10px" }}>
                  <div style={{ fontFamily: C.display, fontWeight: 800, fontSize: 15, color: C.bone }}>{v.title}</div>
                  <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: ".2em", color: v.color, marginTop: 1 }}>{v.label.toUpperCase()}</div>
                  <div style={{ fontFamily: C.sans, fontSize: 11, color: C.ash, marginTop: 5, lineHeight: 1.35 }}>{v.blurb}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// convenience for callers that only have the audio state
export const trackSeed = () => { const s = getAudioState(); return s.track?.id || s.track?.title || "lyraea"; };
